import React, { useEffect, useRef } from "react";
import styles from "./AdSlot.module.scss";

// One Google AdSense ad unit. The AdSense script loads the first time a slot
// mounts (nowhere else), so pages without an ad stay as light as before; it
// also shows Google's consent message to players in the EU and UK.
// A slot with no unit id renders nothing: units are made in the AdSense
// dashboard once the site is approved, then their ids go in AD_UNITS.
// Never on localhost (no invalid clicks or test impressions from us).

const CLIENT = "ca-pub-1046337035997620";
export const AD_UNITS = {
  results: "", // the Results page, under the scores
};

let loading: Promise<void> | null = null;
function loadAdsense(): Promise<void> {
  if (!loading) {
    loading = new Promise((resolve, reject) => {
      const s = document.createElement("script");
      s.async = true;
      s.crossOrigin = "anonymous";
      s.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${CLIENT}`;
      s.onload = () => resolve();
      s.onerror = () => { loading = null; reject(); };
      document.head.appendChild(s);
    });
  }
  return loading;
}

const live = typeof location !== "undefined" && /(^|\.)achtungroyale\.io$/.test(location.hostname);

export default function AdSlot({ unit, className }: { unit: keyof typeof AD_UNITS; className?: string }) {
  const id = AD_UNITS[unit];
  const ref = useRef<HTMLModElement>(null);
  useEffect(() => {
    if (!id || !live) return;
    let gone = false;
    loadAdsense().then(() => {
      if (gone || !ref.current || ref.current.dataset.adsbygoogleStatus) return;
      try { ((window as any).adsbygoogle = (window as any).adsbygoogle || []).push({}); } catch { /* blocked */ }
    }).catch(() => { /* an ad blocker: the space just stays empty */ });
    return () => { gone = true; };
  }, [id]);
  // in development, an empty box where the ad goes (to check the layout)
  if (import.meta.env.DEV && !live) return <div className={`${styles.ad} ${className || ""}`}><span className={styles.label}>Ad</span><div className={`${styles.ins} ${styles.preview}`}>728 x 90</div></div>;
  if (!id || !live) return null;
  return (
    <div className={`${styles.ad} ${className || ""}`}>
      <span className={styles.label}>Ad</span>
      <ins ref={ref} className={`adsbygoogle ${styles.ins}`} data-ad-client={CLIENT} data-ad-slot={id} />
    </div>
  );
}
